import { Link, PerspectiveExpression } from '@coasys/ad4m';

// Ref types for reactive Vue.js properties
export type Ref<T> = { value: T };
export type ComputedRef<T> = { value: T };

export interface NeighbourhoodMeta {
  name: string;
  description: string;
  languages: { [x: string]: string };
}

export interface FileData {
  name: string;
  file_type: string;
  data_base64: string;
}

export interface Community {
  id: string;
  uuid: string;
  author: string;
  timestamp: string;
  name: string;
  description: string;
  image: string | FileData | undefined;
  thumbnail: string | FileData | undefined;
  neighbourhoodUrl: string;
  members: string[];
}

export interface CommunityMetaData {
  name?: string;
  description?: string;
  image?: string;
  thumbnail?: string;
  id?: string;
}

export interface NeighbourhoodMetaData {
  name: string;
  author?: string;
  timestamp?: string;
  description?: string;
}

export interface Channel {
  id: string;
  name: string;
  description: string;
  perspectiveUuid: string;
  timestamp: Date | string;
  author: string; // did
  views: ChannelView[];
}

export interface Reaction {
  author: string;
  content: string;
  timestamp: Date;
  synced: boolean;
}

export interface Message {
  id: string;
  author: string; // did
  content: string;
  reactions: Reaction[];
  timestamp: Date | string;
  reply?: string;
  isPopular: boolean;
  replies: any[];
  isNeighbourhoodCardHidden: boolean;
  editMessages: {
    content: string;
    author: string;
    timestamp: Date | string;
  }[];
  synced: boolean;
}

export interface Messages {
  [x: string]: Message;
}

export interface Profile {
  did: string;
  username: string;
  bio: string;
  email: string;
  givenName: string;
  familyName: string;
  profileBackground: string;
  profileThumbnailPicture: string;
  profilePicture: string;
}
export interface Profiles {
  [x: string]: Profile;
}

export enum ChannelView {
  Chat = 'flux://has_chat_view',
  Post = 'flux://has_post_view',
  Graph = 'flux://has_graph_view',
  Voice = 'flux://has_voice_view',
  Debug = 'flux://has_debug_view',
}

export enum EntryType {
  Community = 'flux://has_community',
  Channel = 'flux://has_channel',
  Message = 'flux://has_message',
  Post = 'flux://has_post',
  Member = 'flux://has_member',
  App = 'flux://has_app',
}

//This represents an entry itself, which contains the default fields seen below
//and is then extended to include other fields depending on the type of entry
export interface Entry {
  id: string;
  author: string;
  timestamp: number;
  type: EntryType;
  source: string;
}

export interface Post extends Entry {
  image: string;
  url: string;
  reactions: Reaction[];
  isPopular: boolean;
  startDate: string;
  endDate: string;
  title: string;
  body: string;
  replies: Post;
}

export interface EntryInput {
  id?: string;
  perspectiveUuid: string;
  source?: string;
  type: EntryType;
  data: PredicateMap;
}

type Target = String;

//Represents the relationship between given predicate to a given expression url
export type PredicateMap = {
  [predicate: string]: Target | Target[];
};

//Represents the relationship between given predicate to any data type,
//this is usually used for data which has not being created as an expression yet
export type PredicateAnyMap = {
  [predicate: string]: any;
};

//Represents the relationship between a given property and its associated value
//This property is expected to be resolved in the Model to find the associated predicate
export type PropertyMap = {
  [property: string]: any;
};

//Represents the relationship between a given property to a given expression url
export type PropertyValueMap = {
  [property: string]: Target | Target[];
};

export interface GetEntry {
  id: string;
}

export interface GetEntries {
  perspectiveUuid: string;
  queries: PrologQuery[];
}

export interface PrologQuery {
  query: string;
  variables: {
    [x: string]: any;
  };
  resultKeys: string[];
}

export interface SdnaVersion {
  version: number;
  timestamp: Date;
}

export interface ModelProperty {
  predicate: string;
  type: StringConstructor | NumberConstructor;
  collection?: boolean;
  languageAddress?: string;
  resolve: boolean;
}

export interface WebLink {
  title: string;
  description: string;
  image: string;
  url: string;
  id: string;
}

export type AgentStatus = 'active' | 'asleep' | 'offline' | 'invisible' | 'busy' | 'unknown';

export interface RouteParams {
  communityId?: string;
  channelId?: string;
  viewId?: string;
}

export interface ProcessingState {
  step: number;
  channelId: string;
  itemIds: string[];
  author: string;
  communityName?: string;
  channelName?: string;
  conversationName?: string;
}

export type MediaSettings = { audioEnabled: boolean; videoEnabled: boolean; screenShareEnabled: boolean };
export interface AgentState {
  status: AgentStatus;
  currentRoute: RouteParams;
  callRoute: RouteParams;
  mediaSettings: MediaSettings;
  processing: ProcessingState | null;
  inCall: boolean;
  aiEnabled: boolean;
  lastUpdate: number;
}

export type CallHealth = 'healthy' | 'warnings' | 'connections-lost';
export type AgentData = Profile & AgentState;
export interface SignallingService {
  signalling: Ref<boolean>;
  agents: Ref<Record<string, AgentState>>;
  setProcessingState: (newState: Partial<ProcessingState> | null) => void;
  getAgentState(did: string): AgentState | undefined;
  startSignalling: () => void;
  stopSignalling: () => void;
  addSignalHandler: (handler: (signal: PerspectiveExpression) => void) => void;
  removeSignalHandler: (handler: (signal: PerspectiveExpression) => void) => void;
  sendSignal: (link: Link) => void;
  getAgentsInChannel: (channelId?: string) => ComputedRef<AgentData[]>;
  getAgentsInCall: (channelId?: string) => ComputedRef<AgentData[]>;
}
